// Anveshan — Matrimony Dashboard (Phases 1/2/3/5)
// Tabs: Matches | Search | My Profile | Preferences | Sent | Received | Chat | Meetings | Privacy
// Palette: ivory (#F7EFE2), maroon (#7A1F2B), gold (#C8A24B)
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Heart, Sparkle, MapPin, Briefcase, X, CheckCircle, UserCircle,
  SlidersHorizontal, PaperPlaneRight, CalendarCheck, Prohibit, Flag, Download,
  Trash, LockKey, ChatCircle, Clock, Translate as TranslateIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MultiImageUploader from "@/components/MultiImageUploader";
import ImageGallery from "@/components/ImageGallery";
import { useLang } from "@/context/LangContext";

// ------- Design tokens -------
const MAROON = "#7A1F2B";
const GOLD = "#C8A24B";
const IVORY = "#F7EFE2";
const INK = "#2A1A1F";

const CREATED_BY = [
  { v: "self", en: "Self", hi: "स्वयं" },
  { v: "father", en: "Father", hi: "पिता" },
  { v: "mother", en: "Mother", hi: "माता" },
  { v: "brother", en: "Brother", hi: "भाई" },
  { v: "sister", en: "Sister", hi: "बहन" },
  { v: "guardian", en: "Guardian", hi: "संरक्षक" },
  { v: "other", en: "Other relative", hi: "अन्य संबंधी" },
];
const MARITAL = ["Never married", "Divorced", "Widowed", "Separated"];
const DIET = ["Vegetarian", "Non-vegetarian", "Vegan", "Eggetarian", "Jain", "No preference"];
const EMPLOYMENT = ["Private", "Government", "Self-employed", "Business", "Not working"];
const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=800&q=80";

const TABS = [
  { k: "matches",  en: "Suggested matches", hi: "सुझाव" },
  { k: "search",   en: "Search",            hi: "खोजें" },
  { k: "profile",  en: "My profile",        hi: "मेरी प्रोफ़ाइल" },
  { k: "prefs",    en: "Preferences",       hi: "पसंद" },
  { k: "sent",     en: "Sent requests",     hi: "भेजे गए" },
  { k: "received", en: "Received",          hi: "प्राप्त" },
  { k: "chat",     en: "Chats",             hi: "चैट" },
  { k: "meetings", en: "Meetings",          hi: "मुलाक़ातें" },
  { k: "privacy",  en: "Privacy",           hi: "निजता" },
];

const label = (lang, en, hi) => (lang === "hi" ? hi : en);

export default function Matrimony() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [tab, setTab] = useState("matches");
  if (!user || user === false) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-center" style={{ color: INK }}>
        <Heart size={40} weight="duotone" style={{ color: MAROON }} className="mx-auto" />
        <h1 className="font-heading text-5xl mt-3" style={{ color: MAROON }}>Anveshan Matrimony</h1>
        <p className="mt-2 text-[#5C564D]">Please log in to use the matrimony dashboard.</p>
      </div>
    );
  }
  return (
    <div style={{ background: `linear-gradient(180deg, ${IVORY} 0%, #F9F6F0 100%)` }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <Header lang={lang} />
        <TabBar tab={tab} setTab={setTab} lang={lang} />
        <div className="mt-6">
          {tab === "matches"  && <MatchesTab lang={lang} />}
          {tab === "search"   && <SearchTab lang={lang} />}
          {tab === "profile"  && <ProfileTab lang={lang} />}
          {tab === "prefs"    && <PrefsTab lang={lang} />}
          {tab === "sent"     && <SentTab lang={lang} />}
          {tab === "received" && <ReceivedTab lang={lang} />}
          {tab === "chat"     && <ChatTab lang={lang} />}
          {tab === "meetings" && <MeetingsTab lang={lang} />}
          {tab === "privacy"  && <PrivacyTab lang={lang} />}
        </div>
      </div>
    </div>
  );
}

function Header({ lang }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <div className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: GOLD }}>
          {label(lang, "Anveshan Matrimony", "अन्वेषण विवाह")}
        </div>
        <h1 className="font-heading text-5xl mt-2 tracking-tight" style={{ color: MAROON }}>
          {label(lang, "Find your forever.", "अपना जीवनसाथी खोजें")}
        </h1>
        <p className="mt-2 max-w-2xl text-[#5C564D]">
          {label(lang,
            "A respectful matrimonial space for the Anveshan community — verified profiles, transparent matching, private chat only after mutual acceptance.",
            "सम्मानजनक विवाह मंच — सत्यापित प्रोफ़ाइल, पारदर्शी मिलान, पारस्परिक स्वीकृति के बाद ही निजी चैट।")}
        </p>
      </div>
    </div>
  );
}

function TabBar({ tab, setTab, lang }) {
  return (
    <div className="mt-6 flex gap-1 border-b flex-wrap" style={{ borderColor: "#E5D9CC" }} data-testid="matri-tabs">
      {TABS.map((t) => (
        <button key={t.k} data-testid={`matri-tab-${t.k}`} onClick={() => setTab(t.k)}
          className={`px-3 sm:px-4 py-2 text-sm font-semibold border-b-2 transition-colors`}
          style={{
            borderColor: tab === t.k ? MAROON : "transparent",
            color: tab === t.k ? MAROON : "#5C564D",
          }}>
          {label(lang, t.en, t.hi)}
        </button>
      ))}
    </div>
  );
}

// ---------- Matches ----------
function MatchesTab({ lang }) {
  const [matches, setMatches] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/matrimony/matches"); setMatches(data); }
      catch (err) { console.error("matches failed", err); toast.error("Could not load matches"); setMatches([]); }
    })();
  }, []);
  if (matches === null) return <Loading />;
  if (matches.length === 0) return <EmptyCard title={label(lang, "No matches yet", "कोई मेल नहीं")} body={label(lang, "Complete your profile and preferences to see ranked matches.", "अपनी प्रोफ़ाइल और पसंद पूर्ण करें।")} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="matches-grid">
      {matches.map((p) => (
        <MatchCard key={p.id} p={p} lang={lang} onOpen={() => setOpen(p)} />
      ))}
      {open && <MatchBreakdownModal profile={open} lang={lang} onClose={() => setOpen(null)} />}
    </div>
  );
}

function MatchCard({ p, lang, onOpen }) {
  const scoreColor = p.match.score >= 75 ? "#2C4A3B" : p.match.score >= 50 ? GOLD : "#8a2a15";
  return (
    <div className="rounded-2xl overflow-hidden border bg-white shadow-sm" style={{ borderColor: "#E5D9CC" }} data-testid={`match-${p.id}`}>
      <div className="aspect-[4/5] relative bg-[#F2EBE5]">
        <div className="w-full h-full flex items-center justify-center">
          <UserCircle size={96} weight="duotone" style={{ color: "#C9B9A3" }} />
        </div>
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(28,27,26,0.78) 100%)" }} />
        <div className="absolute top-3 right-3 rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: scoreColor, color: "#F9F6F0" }} data-testid={`match-score-${p.id}`}>
          {p.match.score}/100
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 text-[#F9F6F0]">
          <div className="font-heading text-xl">{p.full_name}, {p.age}</div>
          <div className="text-xs opacity-90 flex items-center gap-3 mt-1">
            <span className="flex items-center gap-1"><MapPin size={11} /> {p.city || p.location || "—"}</span>
            <span className="flex items-center gap-1"><Briefcase size={11} /> {p.profession || "—"}</span>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="flex flex-wrap gap-1.5">
          {p.community && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: `${GOLD}22`, color: MAROON }}>{p.community}</span>}
          {p.marital_status && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F2EBE5] text-[#5C564D]">{p.marital_status}</span>}
          {p.photo_verified && <span className="text-[11px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: "#DDEBDF", color: "#2C4A3B" }}><CheckCircle size={10} weight="fill" /> {label(lang, "Verified", "सत्यापित")}</span>}
        </div>
        <p className="text-sm text-[#5C564D] mt-2 line-clamp-2">{p.biography || p.about}</p>
        <button onClick={onOpen} data-testid={`btn-why-${p.id}`} className="mt-3 w-full text-sm font-semibold rounded-full py-2 border transition-colors"
          style={{ borderColor: MAROON, color: MAROON }}>
          {label(lang, "Why this match", "यह क्यों?")}
        </button>
      </div>
    </div>
  );
}

function MatchBreakdownModal({ profile, lang, onClose }) {
  const [busy, setBusy] = useState(false);
  const [intro, setIntro] = useState("");
  const bd = profile.match.breakdown || {};
  const criteria = ["age", "location", "education", "profession", "language", "lifestyle", "family", "other"];
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/matrimony/proposals", { to_profile_id: profile.id, intro });
      toast.success(label(lang, "Proposal sent", "प्रस्ताव भेजा गया"));
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal onClose={onClose} testid="match-breakdown">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase font-bold" style={{ color: GOLD }}>{label(lang, "Compatibility", "अनुकूलता")}</div>
          <div className="font-heading text-3xl mt-1" style={{ color: MAROON }}>{profile.full_name}</div>
          <div className="text-xs text-[#5C564D]">{profile.age} · {profile.city || profile.location} · {profile.profession}</div>
        </div>
        <div className="text-right">
          <div className="font-heading text-5xl" style={{ color: MAROON }}>{profile.match.score}</div>
          <div className="text-xs text-[#5C564D]">/ 100</div>
        </div>
      </div>
      <div className="mt-5 space-y-2">
        {criteria.map((k) => {
          const row = bd[k] || { points: 0, weight: 0, note: "—" };
          const pct = row.weight ? (row.points / row.weight) * 100 : 0;
          return (
            <div key={k} className="rounded-xl border p-3" style={{ borderColor: "#E5D9CC" }} data-testid={`crit-${k}`}>
              <div className="flex items-center justify-between text-xs">
                <div className="font-semibold capitalize" style={{ color: INK }}>{k}</div>
                <div className="text-[#5C564D]">{row.points}/{row.weight}</div>
              </div>
              <div className="h-1.5 rounded-full bg-[#F2EBE5] mt-1 overflow-hidden">
                <div style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: MAROON, height: "100%" }} />
              </div>
              <div className="text-[11px] text-[#5C564D] mt-1">{row.note}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 border-t pt-4" style={{ borderColor: "#E5D9CC" }}>
        <div className="text-sm font-semibold" style={{ color: INK }}>{label(lang, "Send a proposal", "प्रस्ताव भेजें")}</div>
        <p className="text-xs text-[#5C564D] mt-1">{label(lang, "No contact details or photos are shared until both sides accept.", "दोनों पक्षों की स्वीकृति के बाद ही संपर्क विवरण साझा होते हैं।")}</p>
        <textarea data-testid="proposal-intro" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} placeholder={label(lang, "Short, respectful introduction (optional)", "सम्मानजनक परिचय (वैकल्पिक)")}
          className="w-full mt-2 px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: "#E5D9CC" }} />
        <button data-testid="btn-send-proposal" disabled={busy} onClick={send} className="mt-3 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: MAROON }}>
          {busy ? "…" : label(lang, "Send proposal", "प्रस्ताव भेजें")}
        </button>
      </div>
    </Modal>
  );
}

// ---------- Search ----------
function SearchTab({ lang }) {
  const [profiles, setProfiles] = useState([]);
  const [filters, setFilters] = useState({ gender: "", community: "", gotra: "", min_age: "", max_age: "" });
  const [meta, setMeta] = useState({ communities: [], gotras: [] });
  const [loading, setLoading] = useState(true);
  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) p[k] = ["min_age", "max_age"].includes(k) ? Number(v) : v; });
    return p;
  }, [filters]);
  useEffect(() => { api.get("/matrimony/communities").then(({ data }) => setMeta(data)).catch((err) => console.error("meta failed", err)); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/matrimony/profiles", { params }).then(({ data }) => setProfiles(data))
      .catch(() => toast.error("Failed to load profiles"))
      .finally(() => setLoading(false));
  }, [params]);
  return (
    <>
      <div className="p-4 rounded-2xl grid grid-cols-2 md:grid-cols-5 gap-2 border" style={{ background: "white", borderColor: "#E5D9CC" }}>
        <select data-testid="s-gender" value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }}>
          <option value="">{label(lang, "All genders", "सभी")}</option>
          <option value="male">{label(lang, "Grooms", "वर")}</option>
          <option value="female">{label(lang, "Brides", "वधु")}</option>
        </select>
        <select data-testid="s-community" value={filters.community} onChange={(e) => setFilters({ ...filters, community: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }}>
          <option value="">{label(lang, "All communities", "सभी समुदाय")}</option>
          {meta.communities.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="s-gotra" value={filters.gotra} onChange={(e) => setFilters({ ...filters, gotra: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }}>
          <option value="">{label(lang, "All gotras", "सभी गोत्र")}</option>
          {meta.gotras.map((g) => <option key={g}>{g}</option>)}
        </select>
        <input data-testid="s-min" type="number" placeholder={label(lang, "Min age", "न्यूनतम आयु")} value={filters.min_age} onChange={(e) => setFilters({ ...filters, min_age: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} />
        <input data-testid="s-max" type="number" placeholder={label(lang, "Max age", "अधिकतम आयु")} value={filters.max_age} onChange={(e) => setFilters({ ...filters, max_age: e.target.value })} className="px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} />
      </div>
      <div className="mt-6">
        {loading ? <Loading /> : profiles.length === 0 ? <EmptyCard title={label(lang, "No profiles", "कोई प्रोफ़ाइल नहीं")} body={label(lang, "Try widening the filters.", "फ़िल्टर बदलें।")} /> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-grid">
            {profiles.map((p) => <SearchCard key={p.id} p={p} lang={lang} />)}
          </div>
        )}
      </div>
    </>
  );
}

function SearchCard({ p, lang }) {
  const [busy, setBusy] = useState(false);
  const propose = async () => {
    setBusy(true);
    try { await api.post("/matrimony/proposals", { to_profile_id: p.id }); toast.success(label(lang, "Proposal sent", "प्रस्ताव भेजा")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl overflow-hidden border bg-white" style={{ borderColor: "#E5D9CC" }} data-testid={`search-card-${p.id}`}>
      <div className="aspect-[4/5] relative">
        <ImageGallery images={p.photos} fallback={p.photo_url || DEFAULT_PHOTO} alt={p.full_name} />
        {!p.photos_visible && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(42,26,31,0.72)", backdropFilter: "blur(14px)" }}>
            <div className="text-center text-[#F9F6F0] px-4">
              <LockKey size={28} weight="duotone" />
              <div className="text-sm mt-2">{label(lang, "Photos private until mutual acceptance", "दोनों पक्षों की स्वीकृति तक गोपनीय")}</div>
            </div>
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="font-heading text-lg" style={{ color: MAROON }}>{p.full_name}, {p.age}</div>
        <div className="text-xs text-[#5C564D] mt-0.5">{p.city || p.location} · {p.profession}</div>
        <div className="flex flex-wrap gap-1 mt-2">
          {p.community && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${GOLD}22`, color: MAROON }}>{p.community}</span>}
          {p.education && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F2EBE5] text-[#5C564D]">{p.education}</span>}
        </div>
        <button disabled={busy} onClick={propose} data-testid={`btn-propose-${p.id}`} className="mt-3 w-full text-sm font-semibold rounded-full py-2 text-white" style={{ background: MAROON }}>
          {label(lang, "Send proposal", "प्रस्ताव भेजें")}
        </button>
      </div>
    </div>
  );
}

// ---------- Profile ----------
function ProfileTab({ lang }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ communities: [], gotras: [] });
  const [form, setForm] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const [{ data: mine }, { data: m }] = await Promise.all([
          api.get("/matrimony/profiles/mine"),
          api.get("/matrimony/communities"),
        ]);
        setP(mine); setMeta(m);
        setForm({
          full_name: "", age: 25, gender: "male", marital_status: "Never married",
          community: "", gotra: "", city: "", state: "", country: "", location: "",
          education: "", profession: "", employment: "", income_usd: "", income_visible: false,
          mother_tongue: "", languages: "", religion: "", height_cm: "",
          family_info: "", biography: "", about: "", hobbies: "", interests: "",
          diet: "", drinks: "Never", smokes: "Never",
          contact_phone: "", contact_email_visible: false,
          created_by_relation: "self", candidate_consent: true,
          photos: [], ...(mine || {}),
          languages: ((mine && mine.languages) || []).join(", "),
          hobbies: ((mine && mine.hobbies) || []).join(", "),
          interests: ((mine && mine.interests) || []).join(", "),
        });
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <Loading />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    if (Number(form.age) < 18) { toast.error(label(lang, "Minimum age is 18", "न्यूनतम आयु 18 है")); return; }
    if (!form.candidate_consent) { toast.error(label(lang, "Candidate consent is required", "सहमति आवश्यक है")); return; }
    const payload = {
      ...form,
      age: Number(form.age) || 18,
      height_cm: form.height_cm ? Number(form.height_cm) : null,
      income_usd: form.income_usd ? Number(form.income_usd) : null,
      languages: splitCsv(form.languages),
      hobbies: splitCsv(form.hobbies),
      interests: splitCsv(form.interests),
      location: form.location || form.city || "",
      about: form.about || form.biography || "",
    };
    try {
      const { data } = await api.put("/matrimony/profiles/mine", payload);
      setP(data); toast.success(label(lang, "Profile saved", "प्रोफ़ाइल सहेजी गई"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="profile-form">
      <SectionCard title={label(lang, "Who is creating this profile?", "यह प्रोफ़ाइल कौन बना रहा है?")}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CREATED_BY.map((r) => (
            <label key={r.v} className={`cursor-pointer p-2 rounded-xl border text-xs text-center ${form.created_by_relation === r.v ? "text-white" : "text-[#5C564D]"}`}
              style={{ background: form.created_by_relation === r.v ? MAROON : "white", borderColor: form.created_by_relation === r.v ? MAROON : "#E5D9CC" }}>
              <input type="radio" name="cb" value={r.v} checked={form.created_by_relation === r.v} onChange={() => set("created_by_relation", r.v)} data-testid={`cb-${r.v}`} className="hidden" />
              {label(lang, r.en, r.hi)}
            </label>
          ))}
        </div>
        <label className="flex items-start gap-2 text-xs text-[#5C564D] mt-3">
          <input type="checkbox" data-testid="consent" checked={!!form.candidate_consent} onChange={(e) => set("candidate_consent", e.target.checked)} className="mt-0.5" />
          <span>{label(lang, "I confirm the candidate has given consent to publish this profile and is at least 18 years old.", "मैं पुष्टि करता/करती हूँ कि उम्मीदवार की सहमति है और आयु 18 वर्ष से अधिक है।")}</span>
        </label>
      </SectionCard>

      <SectionCard title={label(lang, "Basic details", "मूल विवरण")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="f-name" lab={label(lang, "Full name", "पूरा नाम")} val={form.full_name} on={(v) => set("full_name", v)} required />
          <Input testid="f-age" type="number" min={18} lab={label(lang, "Age", "आयु")} val={form.age} on={(v) => set("age", v)} required />
          <Select testid="f-gender" lab={label(lang, "Gender", "लिंग")} val={form.gender} on={(v) => set("gender", v)} options={[["male", label(lang, "Groom", "वर")], ["female", label(lang, "Bride", "वधु")]]} />
          <Select testid="f-marital" lab={label(lang, "Marital status", "वैवाहिक स्थिति")} val={form.marital_status} on={(v) => set("marital_status", v)} options={MARITAL.map((m) => [m, m])} />
          <Input testid="f-height" type="number" lab={label(lang, "Height (cm)", "ऊँचाई (से.मी)")} val={form.height_cm} on={(v) => set("height_cm", v)} />
          <Input testid="f-religion" lab={label(lang, "Religion", "धर्म")} val={form.religion} on={(v) => set("religion", v)} />
          <Input testid="f-city" lab={label(lang, "City", "शहर")} val={form.city} on={(v) => set("city", v)} />
          <Input testid="f-state" lab={label(lang, "State", "राज्य")} val={form.state} on={(v) => set("state", v)} />
          <Input testid="f-country" lab={label(lang, "Country", "देश")} val={form.country} on={(v) => set("country", v)} />
          <Input testid="f-mt" lab={label(lang, "Mother tongue", "मातृभाषा")} val={form.mother_tongue} on={(v) => set("mother_tongue", v)} />
        </div>
      </SectionCard>

      <SectionCard title={label(lang, "Community & languages", "समुदाय और भाषाएँ")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Select testid="f-community" lab={label(lang, "Community", "समुदाय")} val={form.community} on={(v) => set("community", v)} options={[["", "—"], ...meta.communities.map((c) => [c, c])]} required />
          <Select testid="f-gotra" lab={label(lang, "Gotra", "गोत्र")} val={form.gotra || ""} on={(v) => set("gotra", v)} options={[["", "—"], ...meta.gotras.map((g) => [g, g])]} />
          <Input testid="f-langs" lab={label(lang, "Languages (comma-sep)", "भाषाएँ (अल्पविराम से)")} val={form.languages} on={(v) => set("languages", v)} />
          <Input testid="f-hobbies" lab={label(lang, "Hobbies (comma-sep)", "शौक (अल्पविराम से)")} val={form.hobbies} on={(v) => set("hobbies", v)} />
          <Input testid="f-interests" lab={label(lang, "Interests (comma-sep)", "रुचियाँ")} val={form.interests} on={(v) => set("interests", v)} />
        </div>
      </SectionCard>

      <SectionCard title={label(lang, "Education & career", "शिक्षा और करियर")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="f-education" lab={label(lang, "Education", "शिक्षा")} val={form.education} on={(v) => set("education", v)} />
          <Input testid="f-profession" lab={label(lang, "Profession", "व्यवसाय")} val={form.profession} on={(v) => set("profession", v)} />
          <Select testid="f-employment" lab={label(lang, "Employment", "रोज़गार")} val={form.employment || ""} on={(v) => set("employment", v)} options={[["", "—"], ...EMPLOYMENT.map((e) => [e, e])]} />
          <Input testid="f-income" type="number" lab={label(lang, "Annual income (USD, optional)", "वार्षिक आय (USD, वैकल्पिक)")} val={form.income_usd} on={(v) => set("income_usd", v)} />
          <label className="flex items-center gap-2 text-xs text-[#5C564D] sm:col-span-2">
            <input type="checkbox" data-testid="f-income-vis" checked={!!form.income_visible} onChange={(e) => set("income_visible", e.target.checked)} />
            {label(lang, "Show income on my profile", "मेरी प्रोफ़ाइल पर आय दिखाएँ")}
          </label>
        </div>
      </SectionCard>

      <SectionCard title={label(lang, "Family, lifestyle & bio", "परिवार, जीवनशैली और परिचय")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Select testid="f-diet" lab={label(lang, "Diet", "आहार")} val={form.diet || ""} on={(v) => set("diet", v)} options={[["", "—"], ...DIET.map((d) => [d, d])]} />
          <Select testid="f-drinks" lab={label(lang, "Drinks", "मद्यपान")} val={form.drinks || "Never"} on={(v) => set("drinks", v)} options={[["Never", "Never"], ["Occasionally", "Occasionally"], ["Regularly", "Regularly"]]} />
          <Select testid="f-smokes" lab={label(lang, "Smokes", "धूम्रपान")} val={form.smokes || "Never"} on={(v) => set("smokes", v)} options={[["Never", "Never"], ["Occasionally", "Occasionally"], ["Regularly", "Regularly"]]} />
          <TextArea testid="f-family" lab={label(lang, "Family information", "परिवार का विवरण")} val={form.family_info} on={(v) => set("family_info", v)} rows={3} />
          <TextArea testid="f-bio" lab={label(lang, "Biography", "परिचय")} val={form.biography} on={(v) => set("biography", v)} rows={4} />
        </div>
      </SectionCard>

      <SectionCard title={label(lang, "Contact (private by default)", "संपर्क (डिफ़ॉल्ट रूप से निजी)")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input testid="f-phone" lab={label(lang, "Phone", "फ़ोन")} val={form.contact_phone} on={(v) => set("contact_phone", v)} />
          <label className="flex items-center gap-2 text-xs text-[#5C564D]">
            <input type="checkbox" data-testid="f-email-vis" checked={!!form.contact_email_visible} onChange={(e) => set("contact_email_visible", e.target.checked)} />
            {label(lang, "Make email visible to accepted connections", "स्वीकृत संपर्कों को ईमेल दिखाएँ")}
          </label>
        </div>
        <p className="text-[11px] text-[#5C564D] mt-2">{label(lang, "Contact details are shared only after mutual proposal acceptance.", "संपर्क विवरण केवल आपसी स्वीकृति के बाद साझा होते हैं।")}</p>
      </SectionCard>

      <SectionCard title={label(lang, "Photos", "फ़ोटो")}>
        <MultiImageUploader testid="f-photos" label="Profile photos" max={6} value={form.photos || []} onChange={(urls) => set("photos", urls)} />
        <p className="text-[11px] text-[#5C564D] mt-2">{label(lang, "Photos stay private until you approve a photo request.", "अनुमति देने तक फ़ोटो निजी रहती हैं।")}</p>
      </SectionCard>

      <div className="flex justify-end">
        <button type="submit" data-testid="btn-save-profile" className="rounded-full px-6 py-2 text-sm font-semibold text-white" style={{ background: MAROON }}>
          {label(lang, "Save profile", "प्रोफ़ाइल सहेजें")}
        </button>
      </div>
    </form>
  );
}

// ---------- Preferences ----------
function PrefsTab({ lang }) {
  const [prefs, setPrefs] = useState(null);
  const [meta, setMeta] = useState({ communities: [], gotras: [] });
  useEffect(() => {
    (async () => {
      try {
        const [{ data: p }, { data: m }] = await Promise.all([
          api.get("/matrimony/preferences/mine"), api.get("/matrimony/communities"),
        ]);
        setMeta(m);
        setPrefs(p || { age_min: "", age_max: "", height_min_cm: "", height_max_cm: "",
          marital_status: [], education: [], profession: [], locations: [], languages: [],
          communities: [], gotras: [], diet: [], relocation_ok: null, hard_fields: [],
          lifestyle_notes: "", career_notes: "", family_notes: "", other_notes: "" });
      } catch (err) { console.error(err); }
    })();
  }, []);
  if (!prefs) return <Loading />;
  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const toggleTag = (k, tag) => setPrefs((p) => {
    const arr = Array.isArray(p[k]) ? p[k] : [];
    return { ...p, [k]: arr.includes(tag) ? arr.filter((x) => x !== tag) : [...arr, tag] };
  });
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...prefs,
      age_min: numOrNull(prefs.age_min), age_max: numOrNull(prefs.age_max),
      height_min_cm: numOrNull(prefs.height_min_cm), height_max_cm: numOrNull(prefs.height_max_cm),
    };
    try { await api.put("/matrimony/preferences", payload); toast.success(label(lang, "Preferences saved", "पसंद सहेजी गई")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="prefs-form">
      <SectionCard title={label(lang, "Age & height (leave blank = no preference)", "आयु और ऊँचाई (खाली = कोई प्राथमिकता नहीं)")}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Input testid="p-age-min" type="number" lab={label(lang, "Min age", "न्यूनतम आयु")} val={prefs.age_min ?? ""} on={(v) => set("age_min", v)} />
          <Input testid="p-age-max" type="number" lab={label(lang, "Max age", "अधिकतम आयु")} val={prefs.age_max ?? ""} on={(v) => set("age_max", v)} />
          <Input testid="p-h-min" type="number" lab={label(lang, "Min height (cm)", "न्यूनतम ऊँचाई")} val={prefs.height_min_cm ?? ""} on={(v) => set("height_min_cm", v)} />
          <Input testid="p-h-max" type="number" lab={label(lang, "Max height (cm)", "अधिकतम ऊँचाई")} val={prefs.height_max_cm ?? ""} on={(v) => set("height_max_cm", v)} />
        </div>
      </SectionCard>

      <SectionCard title={label(lang, "Preferred values (empty = any)", "पसंद के मान (खाली = कोई भी)")}>
        <TagEditor lab={label(lang, "Marital status", "वैवाहिक स्थिति")} testid="p-marital" options={MARITAL} value={prefs.marital_status} onToggle={(t) => toggleTag("marital_status", t)} />
        <TagEditor lab={label(lang, "Communities", "समुदाय")} testid="p-comm" options={meta.communities} value={prefs.communities} onToggle={(t) => toggleTag("communities", t)} />
        <TagEditor lab={label(lang, "Diet", "आहार")} testid="p-diet" options={DIET} value={prefs.diet} onToggle={(t) => toggleTag("diet", t)} />
        <CSV lab={label(lang, "Education (comma-sep, 'No preference' = empty)", "शिक्षा")} testid="p-edu" val={(prefs.education || []).join(", ")} on={(v) => set("education", splitCsv(v))} />
        <CSV lab={label(lang, "Professions", "व्यवसाय")} testid="p-prof" val={(prefs.profession || []).join(", ")} on={(v) => set("profession", splitCsv(v))} />
        <CSV lab={label(lang, "Locations (cities/states)", "स्थान")} testid="p-locs" val={(prefs.locations || []).join(", ")} on={(v) => set("locations", splitCsv(v))} />
        <CSV lab={label(lang, "Languages", "भाषाएँ")} testid="p-langs" val={(prefs.languages || []).join(", ")} on={(v) => set("languages", splitCsv(v))} />
      </SectionCard>

      <SectionCard title={label(lang, "Hard preferences (must-match)", "अनिवार्य प्राथमिकताएँ")}>
        <p className="text-xs text-[#5C564D] mb-2">{label(lang, "Profiles failing a hard preference are filtered out of your match list.", "अनिवार्य प्राथमिकता पूरी न करने वाली प्रोफ़ाइल सूची में नहीं दिखेंगी।")}</p>
        <TagEditor lab="" testid="p-hard" options={["age", "marital_status", "community", "location"]} value={prefs.hard_fields || []} onToggle={(t) => toggleTag("hard_fields", t)} />
      </SectionCard>

      <SectionCard title={label(lang, "Lifestyle & family notes", "जीवनशैली और पारिवारिक टिप्पणियाँ")}>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextArea testid="p-ls" lab={label(lang, "Lifestyle expectations", "जीवनशैली अपेक्षाएँ")} val={prefs.lifestyle_notes || ""} on={(v) => set("lifestyle_notes", v)} rows={2} />
          <TextArea testid="p-career" lab={label(lang, "Career expectations", "करियर अपेक्षाएँ")} val={prefs.career_notes || ""} on={(v) => set("career_notes", v)} rows={2} />
          <TextArea testid="p-fam" lab={label(lang, "Family preferences", "पारिवारिक पसंद")} val={prefs.family_notes || ""} on={(v) => set("family_notes", v)} rows={2} />
          <TextArea testid="p-other" lab={label(lang, "Other preferences", "अन्य")} val={prefs.other_notes || ""} on={(v) => set("other_notes", v)} rows={2} />
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" data-testid="p-reloc" checked={!!prefs.relocation_ok} onChange={(e) => set("relocation_ok", e.target.checked)} />
            {label(lang, "Open to relocation", "स्थान परिवर्तन के लिए तैयार")}
          </label>
        </div>
      </SectionCard>

      <div className="flex justify-end">
        <button type="submit" data-testid="btn-save-prefs" className="rounded-full px-6 py-2 text-sm font-semibold text-white" style={{ background: MAROON }}>
          {label(lang, "Save preferences", "पसंद सहेजें")}
        </button>
      </div>
    </form>
  );
}

// ---------- Sent / Received proposals ----------
function SentTab({ lang }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/matrimony/proposals/sent"); setRows(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);
  const withdraw = async (id) => {
    try { await api.post("/matrimony/proposals/withdraw", { proposal_id: id, action: "withdrawn" }); toast.success(label(lang, "Withdrawn", "वापस लिया गया")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!rows.length) return <EmptyCard title={label(lang, "No sent proposals", "कोई प्रस्ताव नहीं")} body={label(lang, "Browse matches and send your first proposal.", "मेल देखें और प्रस्ताव भेजें।")} />;
  return (
    <div className="space-y-3" data-testid="sent-list">
      {rows.map((r) => (
        <ProposalRow key={r.id} r={r} lang={lang} isSender actions={
          r.status === "sent" ? [{ k: "withdraw", en: "Withdraw", hi: "वापस लें", on: () => withdraw(r.id), danger: true }] : []
        } />
      ))}
    </div>
  );
}

function ReceivedTab({ lang }) {
  const [rows, setRows] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/matrimony/proposals/received"); setRows(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);
  const decide = async (id, action) => {
    try { await api.post("/matrimony/proposals/decide", { proposal_id: id, action }); toast.success(action); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!rows.length) return <EmptyCard title={label(lang, "No received proposals", "कोई प्राप्त प्रस्ताव नहीं")} body={label(lang, "They will appear here as members propose.", "यहाँ प्रकट होंगे।")} />;
  return (
    <div className="space-y-3" data-testid="received-list">
      {rows.map((r) => (
        <ProposalRow key={r.id} r={r} lang={lang} actions={
          r.status === "sent" ? [
            { k: "accept", en: "Accept", hi: "स्वीकार", on: () => decide(r.id, "accept") },
            { k: "info", en: "Request info", hi: "और जानकारी", on: () => decide(r.id, "info_requested") },
            { k: "decline", en: "Decline", hi: "अस्वीकार", on: () => decide(r.id, "decline"), danger: true },
          ] : []
        } />
      ))}
    </div>
  );
}

function ProposalRow({ r, lang, isSender, actions }) {
  const o = r.other_profile || {};
  const statusColor = {
    sent: GOLD, accepted: "#2C4A3B", declined: "#8a2a15",
    info_requested: "#D49A36", withdrawn: "#5C564D",
  }[r.status] || "#5C564D";
  return (
    <div className="rounded-2xl border bg-white p-4 flex flex-wrap gap-4 items-center" style={{ borderColor: "#E5D9CC" }} data-testid={`prop-row-${r.id}`}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${GOLD}22`, color: MAROON }}>
        <UserCircle size={28} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading text-lg truncate" style={{ color: MAROON }}>{o.full_name || "—"}</div>
        <div className="text-xs text-[#5C564D] truncate">{o.age ? `${o.age} · ` : ""}{o.location || ""}{o.profession ? ` · ${o.profession}` : ""}</div>
        {r.intro && <div className="text-xs italic text-[#5C564D] mt-1 line-clamp-2">"{r.intro}"</div>}
        {r.last_note && <div className="text-[11px] text-[#5C564D] mt-1">{label(lang, "Note", "टिप्पणी")}: {r.last_note}</div>}
      </div>
      <div className="flex flex-col items-end gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: statusColor }}>{r.status.replace("_", " ")}</span>
        <div className="flex gap-1 flex-wrap">
          {(actions || []).map((a) => (
            <button key={a.k} onClick={a.on} data-testid={`btn-${a.k}-${r.id}`}
              className="text-xs font-semibold rounded-full px-3 py-1 border"
              style={{ borderColor: a.danger ? "#8a2a15" : MAROON, color: a.danger ? "#8a2a15" : MAROON }}>
              {label(lang, a.en, a.hi)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------- Chat ----------
function ChatTab({ lang }) {
  const [accepted, setAccepted] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  const load = async () => {
    try {
      const [{ data: sent }, { data: recv }] = await Promise.all([
        api.get("/matrimony/proposals/sent"), api.get("/matrimony/proposals/received")]);
      setAccepted([...sent, ...recv].filter((r) => r.status === "accepted"));
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!active) return;
    let on = true;
    const tick = async () => {
      try { const { data } = await api.get(`/matrimony/messages/${active.id}`); if (on) setMsgs(data); } catch (err) { console.error(err); }
    };
    tick();
    const int = setInterval(tick, 4000);
    return () => { on = false; clearInterval(int); };
  }, [active]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 9e9 }); }, [msgs]);
  const send = async () => {
    if (!text.trim()) return;
    try { await api.post("/matrimony/messages", { proposal_id: active.id, text }); setText(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  if (!accepted.length) return <EmptyCard title={label(lang, "No private chats yet", "कोई निजी चैट नहीं")} body={label(lang, "Chat opens after mutual acceptance.", "आपसी स्वीकृति के बाद चैट खुलती है।")} />;
  return (
    <div className="grid md:grid-cols-[280px_1fr] gap-4" data-testid="chat">
      <div className="rounded-2xl border bg-white max-h-[70vh] overflow-y-auto" style={{ borderColor: "#E5D9CC" }}>
        {accepted.map((r) => {
          const o = r.other_profile || {};
          const isActive = active?.id === r.id;
          return (
            <button key={r.id} onClick={() => { setActive(r); setMsgs([]); }} data-testid={`chat-thread-${r.id}`}
              className="w-full text-left px-3 py-3 border-b flex items-center gap-3" style={{ borderColor: "#F0E6D7", background: isActive ? `${GOLD}15` : "white" }}>
              <UserCircle size={28} weight="duotone" style={{ color: MAROON }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: INK }}>{o.full_name || "—"}</div>
                <div className="text-[11px] text-[#5C564D] truncate">{o.location || ""}</div>
              </div>
            </button>
          );
        })}
      </div>
      <div className="rounded-2xl border bg-white flex flex-col max-h-[70vh]" style={{ borderColor: "#E5D9CC" }}>
        {!active ? (
          <div className="p-6 text-sm text-[#5C564D]">{label(lang, "Pick a conversation.", "वार्तालाप चुनें।")}</div>
        ) : (
          <>
            <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: "#F0E6D7" }}>
              <ChatCircle size={18} style={{ color: MAROON }} />
              <div className="text-sm font-semibold" style={{ color: INK }}>{active.other_profile?.full_name}</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-pane">
              {msgs.map((m) => {
                const mine = m.author_id;
                return (
                  <div key={m.id} className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.author_id === mine ? "ml-auto text-white" : ""}`}
                    style={{ background: m.author_id ? MAROON : "#F2EBE5", color: "#F9F6F0" }}>
                    <div>{m.text}</div>
                    <div className="text-[10px] opacity-75 mt-0.5">{new Date(m.created_at).toLocaleTimeString()}</div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t flex gap-2" style={{ borderColor: "#F0E6D7" }}>
              <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={label(lang, "Type a message…", "संदेश लिखें…")} className="flex-1 px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} />
              <button data-testid="chat-send" onClick={send} className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: MAROON }}>
                <PaperPlaneRight size={14} weight="fill" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- Meetings ----------
function MeetingsTab({ lang }) {
  const [meetings, setMeetings] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ proposal_id: "", scheduled_at: "", venue: "", mode: "in-person" });
  const load = async () => {
    try {
      const [{ data: m }, { data: s }, { data: r }] = await Promise.all([
        api.get("/matrimony/meetings/mine"), api.get("/matrimony/proposals/sent"), api.get("/matrimony/proposals/received"),
      ]);
      setMeetings(m);
      setAccepted([...s, ...r].filter((p) => p.status === "accepted"));
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);
  const propose = async (e) => {
    e.preventDefault();
    try {
      await api.post("/matrimony/meetings", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success(label(lang, "Meeting proposed", "मुलाक़ात प्रस्तावित"));
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const respond = async (id, action) => {
    try { await api.post("/matrimony/meetings/respond", { meeting_id: id, action }); toast.success(action); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-[#5C564D]">{label(lang, "Meetings scheduled with accepted connections.", "स्वीकृत संपर्कों से तय मुलाक़ातें")}</div>
        <button disabled={!accepted.length} onClick={() => setOpen(true)} data-testid="btn-propose-meeting" className="rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: MAROON }}>
          <CalendarCheck size={14} className="inline mr-1" /> {label(lang, "Propose meeting", "मुलाक़ात प्रस्ताव")}
        </button>
      </div>
      {!meetings.length ? <EmptyCard title={label(lang, "No meetings yet", "कोई मुलाक़ात नहीं")} body={label(lang, "After acceptance you can schedule a mutual meeting.", "स्वीकृति के बाद मुलाक़ात तय करें।")} /> : (
        <div className="space-y-3" data-testid="meetings-list">
          {meetings.map((m) => (
            <div key={m.id} className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E5D9CC" }} data-testid={`meeting-${m.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs font-bold uppercase tracking-wider" style={{ color: GOLD }}>{m.mode}</div>
                <span className="text-[10px] font-bold uppercase" style={{ color: m.status === "accepted" ? "#2C4A3B" : m.status === "cancelled" ? "#8a2a15" : GOLD }}>{m.status}</span>
              </div>
              <div className="font-heading text-lg mt-1" style={{ color: MAROON }}><Clock size={14} className="inline mr-1" /> {new Date(m.scheduled_at).toLocaleString()}</div>
              <div className="text-xs text-[#5C564D] mt-1"><MapPin size={11} className="inline mr-1" /> {m.venue || "—"}</div>
              {m.status === "proposed" && (
                <div className="mt-2 flex gap-2 flex-wrap">
                  <button data-testid={`m-accept-${m.id}`} onClick={() => respond(m.id, "accept")} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#2C4A3B", color: "#2C4A3B" }}>{label(lang, "Accept", "स्वीकार")}</button>
                  <button data-testid={`m-cancel-${m.id}`} onClick={() => respond(m.id, "cancel")} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: "#8a2a15", color: "#8a2a15" }}>{label(lang, "Cancel", "रद्द")}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {open && (
        <Modal testid="meeting-modal" onClose={() => setOpen(false)}>
          <div className="font-heading text-2xl" style={{ color: MAROON }}>{label(lang, "Propose a meeting", "मुलाक़ात प्रस्तावित करें")}</div>
          <form onSubmit={propose} className="mt-4 space-y-3">
            <select required data-testid="m-prop" value={form.proposal_id} onChange={(e) => setForm({ ...form, proposal_id: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }}>
              <option value="">{label(lang, "Choose a connection", "संपर्क चुनें")}</option>
              {accepted.map((a) => <option key={a.id} value={a.id}>{a.other_profile?.full_name}</option>)}
            </select>
            <input required type="datetime-local" data-testid="m-when" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} />
            <input data-testid="m-venue" placeholder={label(lang, "Venue", "स्थान")} value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} />
            <select data-testid="m-mode" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }}>
              <option value="in-person">In-person</option>
              <option value="video">Video</option>
            </select>
            <button data-testid="btn-propose-meet-send" className="rounded-full px-4 py-2 text-sm font-semibold text-white w-full" style={{ background: MAROON }}>
              {label(lang, "Send proposal", "प्रस्ताव भेजें")}
            </button>
          </form>
        </Modal>
      )}
    </>
  );
}

// ---------- Privacy ----------
function PrivacyTab({ lang }) {
  const [blocks, setBlocks] = useState([]);
  const [reportOpen, setReportOpen] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/matrimony/blocks"); setBlocks(data); }
    catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, []);
  const closeProfile = async () => {
    if (!window.confirm(label(lang, "Close your profile? It will stop appearing in matches.", "अपनी प्रोफ़ाइल बंद करें? यह मेल में नहीं दिखेगी।"))) return;
    try { await api.post("/matrimony/close-profile"); toast.success(label(lang, "Profile closed", "प्रोफ़ाइल बंद")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const deleteProfile = async () => {
    if (!window.confirm(label(lang, "Permanently delete all matrimony data? This cannot be undone.", "सभी विवाह डेटा स्थायी रूप से हटाएँ?"))) return;
    try { await api.delete("/matrimony/profile"); toast.success(label(lang, "Deleted", "हटाया गया")); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const exportData = async () => {
    try {
      const { data } = await api.get("/matrimony/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `anveshan-matrimony-export-${Date.now()}.json`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const unblock = async (uid) => {
    try { await api.post("/matrimony/unblock", { user_id: uid }); toast.success(label(lang, "Unblocked", "अनब्लॉक")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <SectionCard title={label(lang, "Your data", "आपका डेटा")}>
        <div className="flex gap-2 flex-wrap">
          <button onClick={exportData} data-testid="btn-export" className="rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: MAROON, color: MAROON }}><Download size={14} className="inline mr-1" /> {label(lang, "Export my data", "डेटा निर्यात")}</button>
          <button onClick={closeProfile} data-testid="btn-close" className="rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: GOLD, color: "#7a5a10" }}>{label(lang, "Close profile", "प्रोफ़ाइल बंद")}</button>
          <button onClick={deleteProfile} data-testid="btn-delete" className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "#8a2a15" }}><Trash size={14} className="inline mr-1" /> {label(lang, "Delete matrimony data", "डेटा हटाएँ")}</button>
          <button onClick={() => setReportOpen(true)} data-testid="btn-report" className="rounded-full px-4 py-2 text-sm font-semibold border" style={{ borderColor: "#8a2a15", color: "#8a2a15" }}><Flag size={14} className="inline mr-1" /> {label(lang, "Report an issue", "शिकायत करें")}</button>
        </div>
      </SectionCard>
      <SectionCard title={label(lang, "Blocked users", "अवरुद्ध उपयोगकर्ता")}>
        {!blocks.length ? <div className="text-xs text-[#5C564D]">{label(lang, "No blocked users.", "कोई नहीं")}</div> : (
          <div className="space-y-1">
            {blocks.map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm" data-testid={`block-${b.id}`}>
                <span className="font-mono text-xs text-[#5C564D]">{b.blocked_id}</span>
                <button onClick={() => unblock(b.blocked_id)} data-testid={`unblock-${b.blocked_id}`} className="text-xs rounded-full px-3 py-1 border" style={{ borderColor: MAROON, color: MAROON }}>Unblock</button>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      {reportOpen && <ReportModal lang={lang} onClose={() => setReportOpen(false)} />}
    </div>
  );
}

function ReportModal({ lang, onClose }) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [targetUser, setTargetUser] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/matrimony/report", { reason, details, target_user_id: targetUser || null }); toast.success(label(lang, "Report filed", "शिकायत दर्ज")); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <Modal onClose={onClose} testid="report-modal">
      <div className="font-heading text-2xl" style={{ color: MAROON }}>{label(lang, "Report an issue", "शिकायत करें")}</div>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input required placeholder={label(lang, "Reason", "कारण")} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} data-testid="r-reason" />
        <input placeholder={label(lang, "Target user id (optional)", "लक्ष्य उपयोगकर्ता (वैकल्पिक)")} value={targetUser} onChange={(e) => setTargetUser(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} data-testid="r-user" />
        <textarea rows={4} placeholder={label(lang, "Details", "विवरण")} value={details} onChange={(e) => setDetails(e.target.value)} className="w-full px-3 py-2 rounded-xl border text-sm" style={{ borderColor: "#E5D9CC" }} data-testid="r-details" />
        <button className="rounded-full px-4 py-2 text-sm font-semibold text-white w-full" style={{ background: MAROON }} data-testid="r-submit">{label(lang, "Submit report", "शिकायत भेजें")}</button>
      </form>
    </Modal>
  );
}

// ---------- Shared UI ----------
function SectionCard({ title, children }) {
  return (
    <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E5D9CC" }}>
      <div className="text-sm font-bold tracking-wide" style={{ color: MAROON }}>{title}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}
function Loading() { return <div className="p-10 text-center text-[#5C564D]">Loading…</div>; }
function EmptyCard({ title, body }) {
  return (
    <div className="rounded-2xl border bg-white p-10 text-center" style={{ borderColor: "#E5D9CC" }}>
      <Heart size={28} weight="duotone" style={{ color: MAROON }} className="mx-auto" />
      <div className="font-heading text-2xl mt-2" style={{ color: MAROON }}>{title}</div>
      <p className="text-sm text-[#5C564D] mt-1">{body}</p>
    </div>
  );
}
function Modal({ onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="rounded-2xl bg-[#F9F6F0] max-w-xl w-full p-6 my-8 relative" data-testid={testid} style={{ border: `1px solid ${GOLD}` }}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-[#F2EBE5]" data-testid="modal-close"><X size={18} /></button>
        {children}
      </div>
    </div>
  );
}
function Input({ lab, val, on, type = "text", required, min, testid }) {
  return (
    <label className="block text-xs">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <input data-testid={testid} type={type} required={required} min={min} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: "#E5D9CC" }} />
    </label>
  );
}
function Select({ lab, val, on, options, required, testid }) {
  return (
    <label className="block text-xs">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <select data-testid={testid} required={required} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: "#E5D9CC" }}>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
function TextArea({ lab, val, on, rows = 3, testid }) {
  return (
    <label className="block text-xs sm:col-span-2">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <textarea data-testid={testid} rows={rows} value={val ?? ""} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: "#E5D9CC" }} />
    </label>
  );
}
function CSV({ lab, val, on, testid }) {
  return (
    <label className="block text-xs mt-2">
      <div className="text-[#5C564D] mb-1">{lab}</div>
      <input data-testid={testid} value={val} onChange={(e) => on(e.target.value)}
        className="w-full px-3 py-2 rounded-xl border bg-white text-sm" style={{ borderColor: "#E5D9CC" }} />
    </label>
  );
}
function TagEditor({ lab, options, value, onToggle, testid }) {
  return (
    <div className="text-xs mt-2">
      {lab && <div className="text-[#5C564D] mb-1">{lab}</div>}
      <div className="flex flex-wrap gap-1.5" data-testid={testid}>
        {(options || []).map((o) => {
          const on = (value || []).includes(o);
          return (
            <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testid}-${o}`}
              className="px-2.5 py-1 rounded-full text-xs font-semibold border"
              style={{ background: on ? MAROON : "white", color: on ? "white" : "#5C564D", borderColor: on ? MAROON : "#E5D9CC" }}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
function splitCsv(s) { return (s || "").split(",").map((x) => x.trim()).filter(Boolean); }
function numOrNull(v) { if (v === "" || v === null || v === undefined) return null; const n = Number(v); return Number.isNaN(n) ? null : n; }
